import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../../../helpers'
import { CompanyStatus } from '../../../models'
import {
    BulkUpdatePropertyStatusResponse,
    CompanyWithInvoiceCode,
} from '../Property/types'
import { UpdatePropertyRequest } from './useProperties'

export const ADMIN_PROPERTIES_LIST_KEY = 'admin-properties-list'

export interface PaginatedPropertiesResponse {
    count: number
    next: string | null
    previous: string | null
    results: CompanyWithInvoiceCode[]
}

export interface AdminPropertiesFilterParams {
    search?: string
    organizationId?: number | null
    state?: string | null
    status?: CompanyStatus | 'ALL'
    userEmail?: string
    hasIntegrationError?: boolean
    hasBillingSetup?: boolean
    ordering?: string
}

export interface AdminPropertiesListParams extends AdminPropertiesFilterParams {
    page: number
    pageSize: number
}

export interface AdminPropertiesBulkSelectResponse {
    count: number
    results: CompanyWithInvoiceCode[]
    truncated: boolean
    selected_count: number
}

export type { BulkUpdatePropertyStatusResponse } from '../Property/types'

const buildFilterParams = (
    params: AdminPropertiesFilterParams,
): URLSearchParams => {
    const queryParams = new URLSearchParams()

    if (params.search?.trim()) {
        queryParams.append('search', params.search.trim())
    }
    if (params.organizationId != null) {
        queryParams.append('organization_id', params.organizationId.toString())
    }
    if (params.state?.trim()) {
        queryParams.append('state', params.state.trim())
    }
    if (params.status && params.status !== 'ALL') {
        queryParams.append('status', params.status)
    }
    if (params.userEmail?.trim()) {
        queryParams.append('user_email', params.userEmail.trim())
    }
    if (params.hasIntegrationError) {
        queryParams.append('has_integration_error', 'true')
    }
    if (params.hasBillingSetup) {
        queryParams.append('has_billing_setup', 'true')
    }
    if (params.ordering) {
        queryParams.append('ordering', params.ordering)
    }

    return queryParams
}

const buildListParams = (
    params: AdminPropertiesListParams,
): URLSearchParams => {
    const queryParams = buildFilterParams(params)
    queryParams.append('page', params.page.toString())
    queryParams.append('page_size', params.pageSize.toString())
    return queryParams
}

export const fetchAdminPropertiesBulkSelect = async (
    params: AdminPropertiesFilterParams,
): Promise<AdminPropertiesBulkSelectResponse> => {
    const response = await axiosInstance.get<AdminPropertiesBulkSelectResponse>(
        `/account-manager/properties/admin-list/bulk-select/?${buildFilterParams(
            params,
        ).toString()}`,
    )
    return response.data
}

export const useAdminPropertiesList = (params: AdminPropertiesListParams) => {
    const queryClient = useQueryClient()

    const { data: response, isLoading, isFetching, isError, error } = useQuery<
        AxiosResponse<PaginatedPropertiesResponse>,
        Error
    >({
        queryKey: [ADMIN_PROPERTIES_LIST_KEY, params],
        queryFn: () =>
            axiosInstance.get(
                `/account-manager/properties/admin-list/?${buildListParams(
                    params,
                ).toString()}`,
            ),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const invalidateList = () => {
        queryClient.invalidateQueries({ queryKey: [ADMIN_PROPERTIES_LIST_KEY] })
    }

    const updatePropertyBilling = async (req: UpdatePropertyRequest) => {
        const url = `/account-manager/properties/${req.params.id}/update-billing-info/`
        const res = await axiosInstance.post(url, req.body)
        invalidateList()
        return res.data as CompanyWithInvoiceCode
    }

    const updatePropertyStatusMutation = useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: number
            status: CompanyStatus
        }) => {
            const res = await axiosInstance.patch(
                `/account-manager/properties/${id}/`,
                { status },
            )
            return res.data
        },
        onSuccess: () => {
            invalidateList()
        },
    })

    const updatePropertyStatus = async (id: number, status: CompanyStatus) => {
        return updatePropertyStatusMutation.mutateAsync({ id, status })
    }

    const bulkUpdatePropertyStatus = async (
        propertyIds: number[],
        status: CompanyStatus,
    ): Promise<BulkUpdatePropertyStatusResponse> => {
        const res = await axiosInstance.post<BulkUpdatePropertyStatusResponse>(
            '/account-manager/properties/admin-list/bulk-update-status/',
            { property_ids: propertyIds, status },
        )
        invalidateList()
        return res.data
    }

    const data = response?.data

    return {
        properties: data?.results ?? [],
        count: data?.count ?? 0,
        isLoading,
        isFetching,
        isError,
        error,
        updatePropertyBilling,
        updatePropertyStatus,
        bulkUpdatePropertyStatus,
        invalidateList,
    }
}
